/**
 * The complaint form (BEST-558, ADR 0016, sections/bestio-complaint-form.liquid).
 *
 * Two steps without a sign-in, against the public complaints API of the Platform, without cookies:
 *   POST /api/public/v1/complaints/lookup  {orderName, email}
 *     200 {orderName, orderedAt, marketCode, items: [{lineItemId, title, variantTitle, sku, quantity, imageUrl?}], businessBuyer?}
 *     | 422 complaint_order_not_found | 422 complaint_invalid | 429 | 503
 *   POST /api/public/v1/complaints  {submissionId, orderName, email, customerName, phone?, items: [{lineItemId, quantity}],
 *       defectDescription, defectDiscoveredOn? (yyyy-MM-dd), requestedRemedy?, bankAccount?}
 *     201 {reference, receivedAt, orderName, dueOn} | 200 the same again | 409 | 422 complaint_invalid
 *     | 422 complaint_order_not_found | 429 complaint_limit | 503
 * The server verifies the order, decides the time of the receipt and the deadline; the client checks the same limits only
 * to answer sooner. The form always asks for the remedy (§ 19 odst. 2 ZOS puts it into the confirmation), though the API
 * keeps it optional for complaints the staff record by hand. It takes no photos: the receipt asks for them by e-mail with
 * the complaint number. A submission id per complaint makes a double click, a reload or a retry after a lost answer one
 * complaint; it lives in sessionStorage until the receipt (bestio-public-form.js, loaded first). When the API cannot be
 * reached, the form offers the e-mail with the complaint filled in and the post, which always stay open. The decisions
 * (createComplaintFlow) are separate from the page (createDomView), so the contract test drives them with a fake fetch and
 * a recording view.
 */
(() => {
  const {format, text, NetworkError, createApi, outcomeOf: publicOutcome, createSubmissions: publicSubmissions} = window.BestioPublicForm;
  const API_PATH = '/api/public/v1/complaints';
  const STORAGE_KEY = 'bestio-complaint-submissions';
  const LIMITS = {orderName: 12, email: 254, customerName: 120, phone: 32, defectDescription: 2000, bankAccount: 64};
  /** requestedRemedy of the API, in the order of the section's choices. */
  const REMEDIES = ['Repair', 'Replacement', 'PriceReduction', 'Refund'];
  const REMEDY_KEYS = {Repair: 'remedy_repair', Replacement: 'remedy_replacement', PriceReduction: 'remedy_price_reduction', Refund: 'remedy_refund'};
  /** Remedies that pay money back, so the form asks for the account. */
  const MONEY_REMEDIES = ['PriceReduction', 'Refund'];
  /**
   * Markets whose law wants the customer told before the defect is removed about the choice of repair or replacement and
   * the 12 more months after a repair (SK § 623 ods. 2, § 619 ods. 4 OZ), and to raise a defect within 2 months of
   * finding it (§ 621 ods. 3). By the order's market, not the page language.
   */
  const REPAIR_CHOICE_NOTICE_MARKETS = ['SK'];
  /**
   * The longest fallback e-mail link: some mail handlers (Outlook on Windows) cut or ignore a mailto link over about 2000
   * characters, and Czech diacritics take 6 characters each once encoded. The description, then the goods, are shortened
   * until the link fits; the customer completes them in the mail.
   */
  const MAILTO_LIMIT = 1900;
  const STEP_ONE = ['orderName', 'email', 'customerName', 'phone'];
  const STEP_TWO = ['defectDescription', 'defectDiscoveredOn', 'requestedRemedy', 'bankAccount'];
  const FIELD_CODES = {orderName: 'order', email: 'email', customerName: 'name', phone: 'phone', defectDescription: 'description',
    defectDiscoveredOn: 'date', requestedRemedy: 'remedy', bankAccount: 'account'};
  // eslint-disable-next-line no-control-regex
  const CONTROL = /[\u0000-\u001f\u007f]/;
  // eslint-disable-next-line no-control-regex
  const CONTROL_EXCEPT_LINES = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;

  /** What an answer means for the form; the codes are the API's (PublicComplaintsApi). */
  const outcomeOf = (status, data) => publicOutcome(status, data, 'complaint');

  /** The calendar day of an instant in the shop's time zone (yyyy-MM-dd), or '' for something that is no time. */
  function shopDay(instant) {
    const date = instant instanceof Date ? instant : new Date(instant);
    if (Number.isNaN(date.getTime())) return '';
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {timeZone: 'Europe/Prague', year: 'numeric', month: '2-digit', day: '2-digit'})
      .formatToParts(date).map(part => [part.type, part.value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  /** A real calendar day written as yyyy-MM-dd (no 2026-02-30). */
  function isDay(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }

  /** The first step as the API takes it, or the field errors (field → code). */
  function validateDetails(values) {
    const value = {
      orderName: text(values.orderName).trim(),
      email: text(values.email).trim(),
      customerName: text(values.customerName).trim().replace(/\s+/g, ' '),
      phone: text(values.phone).trim().replace(/\s+/g, ' '),
    };
    const errors = {};
    if (!value.orderName) errors.orderName = 'required';
    else if (!/^#?\d{1,9}$/.test(value.orderName)) errors.orderName = 'order';
    // Sent as Shopify names the order (#1324), which is also how the server keeps it for a repeated submission.
    else value.orderName = `#${value.orderName.replace(/^#/, '')}`;
    if (!value.email) errors.email = 'required';
    else if (value.email.length > LIMITS.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) errors.email = 'email';
    if (!value.customerName) errors.customerName = 'required';
    else if (value.customerName.length > LIMITS.customerName) errors.customerName = 'too_long';
    else if (CONTROL.test(value.customerName)) errors.customerName = 'name';
    if (value.phone) {
      const digits = value.phone.replace(/\D/g, '').length;
      if (value.phone.length > LIMITS.phone) errors.phone = 'too_long';
      // The characters and digits ComplaintRules.Phone takes: digits, space, plus, slash, dash and brackets, 6 to 15 digits.
      else if (!/^[0-9 +()/-]+$/.test(value.phone) || digits < 6 || digits > 15) errors.phone = 'phone';
    }
    return {value, errors};
  }

  /**
   * The second step as the API takes it, or the field errors. `orderDay` and `today` bound the day the defect was found:
   * not before the order, not in the future (both days in the shop's time zone, as the server counts them).
   */
  function validateComplaint(values, {orderDay, today}) {
    const remedy = REMEDIES.includes(values.requestedRemedy) ? values.requestedRemedy : '';
    const value = {
      defectDescription: text(values.defectDescription).replace(/\r\n?/g, '\n').trim(),
      defectDiscoveredOn: text(values.defectDiscoveredOn).trim(),
      requestedRemedy: remedy,
      bankAccount: MONEY_REMEDIES.includes(remedy) ? text(values.bankAccount).trim().replace(/\s+/g, ' ') : '',
    };
    const errors = {};
    if (!value.defectDescription) errors.defectDescription = 'required';
    else if (value.defectDescription.length > LIMITS.defectDescription) errors.defectDescription = 'too_long';
    else if (CONTROL_EXCEPT_LINES.test(value.defectDescription)) errors.defectDescription = 'description';
    if (value.defectDiscoveredOn && (!isDay(value.defectDiscoveredOn)
      || (today && value.defectDiscoveredOn > today) || (orderDay && value.defectDiscoveredOn < orderDay))) {
      errors.defectDiscoveredOn = 'date';
    }
    if (!value.requestedRemedy) errors.requestedRemedy = 'remedy';
    if (value.bankAccount && (value.bankAccount.length > LIMITS.bankAccount || !/^[A-Za-z0-9 /-]+$/.test(value.bankAccount))) {
      errors.bankAccount = 'account';
    }
    return {value, errors};
  }

  /** The order of the lookup as the form uses it, or null when the answer is not the contract (shown as a failure). */
  function normalizeOrder(data) {
    if (!data || typeof data !== 'object' || !Array.isArray(data.items) || typeof data.orderName !== 'string') return null;
    return {
      orderName: data.orderName,
      orderedAt: text(data.orderedAt),
      marketCode: text(data.marketCode).toUpperCase(),
      // An order with a company: the consumer's notice of the market's law is left out, as the confirmation leaves it out.
      businessBuyer: data.businessBuyer === true,
      items: data.items
        .filter(item => item && typeof item.lineItemId === 'string' && typeof item.title === 'string'
          && Number.isInteger(item.quantity) && item.quantity > 0)
        .map(item => ({
          lineItemId: item.lineItemId,
          title: item.title,
          variantTitle: text(item.variantTitle) || null,
          sku: text(item.sku) || null,
          quantity: item.quantity,
          // Only an https image (the Shopify CDN); anything else is left out rather than loaded.
          imageUrl: /^https:\/\//.test(text(item.imageUrl)) ? item.imageUrl : null,
        })),
    };
  }

  /** The complained goods: the ticked items with their pieces. Nothing ticked is an error; a complaint names its goods. */
  function complainedItems(order, selection) {
    const chosen = order.items
      .map(item => ({item, pick: selection.find(entry => entry.lineItemId === item.lineItemId)}))
      .filter(({pick}) => pick?.checked);
    if (chosen.length === 0) return {error: 'nothing_selected'};
    return {
      items: chosen.map(({item, pick}) => ({
        lineItemId: item.lineItemId,
        quantity: Math.min(item.quantity, Math.max(1, Number(pick.quantity ?? 1) || 1)),
      })),
    };
  }

  /** The complaint as one stable text, so the same complaint sent again reuses its submission id. */
  const fingerprint = (details, complaint, items) => JSON.stringify([details.orderName.replace(/^#/, ''), details.email.toLowerCase(),
    details.customerName, details.phone, items, complaint.defectDescription, complaint.defectDiscoveredOn,
    complaint.requestedRemedy, complaint.bankAccount]);

  /** Submission ids by complaint until the receipt (bestio-public-form.js). */
  const createSubmissions = ({storage, uuid}) => publicSubmissions({storage, storageKey: STORAGE_KEY, uuid});

  const itemLabel = item => (item.variantTitle ? `${item.title} (${item.variantTitle})` : item.title);

  /** The e-mail the customer can send instead, with whatever of the complaint the form already has. */
  function mailtoHref(supportEmail, t, {details = {}, complaint = {}, order = null, items = null} = {}) {
    const goods = order && items
      ? items.map(pick => {
        const item = order.items.find(entry => entry.lineItemId === pick.lineItemId);
        return item ? `${itemLabel(item)} × ${pick.quantity}` : '';
      }).filter(Boolean)
      : [];
    // By code points: a cut between the two halves of an emoji is a lone surrogate, which encodeURIComponent refuses.
    const description = Array.from(text(complaint.defectDescription));
    const cut = (value, length) => (value.length > length ? `${value.slice(0, length).join('')}…` : value.join(''));
    const href = (descriptionLength, goodsCount) => {
      const params = {
        order: details.orderName || '…',
        name: details.customerName || '…',
        phone: details.phone || '—',
        items: (goodsCount < goods.length ? [...goods.slice(0, goodsCount), '…'] : goods).join('; ') || '…',
        description: cut(description, descriptionLength) || '…',
        discovered: complaint.defectDiscoveredOn || '—',
        remedy: complaint.requestedRemedy ? t(REMEDY_KEYS[complaint.requestedRemedy]) : '…',
        account: complaint.bankAccount || '—',
      };
      try {
        return `mailto:${supportEmail}?subject=${encodeURIComponent(format(t('mail_subject'), params))}`
          + `&body=${encodeURIComponent(format(t('mail_body'), params))}`;
      } catch {
        // A text the browser cannot encode (a lone surrogate in a name) still leaves the e-mail open, without the body.
        return `mailto:${supportEmail}`;
      }
    };
    let descriptionLength = description.length;
    let goodsCount = goods.length;
    let link = href(descriptionLength, goodsCount);
    while (link.length > MAILTO_LIMIT && descriptionLength > 0) {
      descriptionLength = Math.max(0, Math.min(descriptionLength, description.length) - 50);
      link = href(descriptionLength, goodsCount);
    }
    while (link.length > MAILTO_LIMIT && goodsCount > 0) {
      goodsCount -= 1;
      link = href(descriptionLength, goodsCount);
    }
    return link;
  }

  /** The e-mail with the photos of the defect after the receipt, with the complaint number in the subject. */
  const photosHref = (supportEmail, t, params) => `mailto:${supportEmail}?subject=${encodeURIComponent(format(t('photos_mail_subject'), params))}`
    + `&body=${encodeURIComponent(format(t('photos_mail_body'), params))}`;

  /**
   * Field errors of a 422 in the form's fields: the first step's, the second step's, and one for the goods. The codes are
   * the API's (WithdrawalValidationCodes, ComplaintValidationCodes): required, too_long, invalid, out_of_date_range on the
   * day, no_items on the list, not_in_order, duplicate and out_of_range on an item. A code the form does not know is the
   * general sentence at its field; a key without a field (submissionId) is left to the caller, which shows a failure.
   */
  function serverFieldErrors(data) {
    const errors = {};
    for (const [key, codes] of Object.entries(data?.errors || {})) {
      const code = Array.isArray(codes) ? codes[0] : '';
      if (key === 'items' && ['required', 'no_items'].includes(code)) errors.items = 'nothing_selected';
      else if (key === 'items' || /^items\[\d+\]\./.test(key)) errors.items = 'items_invalid';
      else if (FIELD_CODES[key]) {
        if (code === 'required') errors[key] = key === 'requestedRemedy' ? 'remedy' : 'required';
        else if (code === 'too_long') errors[key] = 'too_long';
        else if (code === 'invalid' || (code === 'out_of_date_range' && key === 'defectDiscoveredOn')) errors[key] = FIELD_CODES[key];
        else errors[key] = 'invalid';
      }
    }
    return errors;
  }

  const pick = (errors, names) => Object.fromEntries(Object.entries(errors).filter(([name]) => names.includes(name)));

  function createComplaintFlow({api, view, submissions, supportEmail, t, formatDate, today = () => shopDay(new Date())}) {
    const state = {details: null, order: null, busy: false};

    // Every refusal that leaves the customer without an online receipt offers the e-mail with the complaint filled in: a
    // mistyped order, an order without goods, the daily limit of the order, an outage or too many tries. The right to
    // complain never hangs on the form.
    const failure = (kind, context = {}) => view.showMessage(kind, {
      fallback: ['failed', 'rate_limited', 'not_found', 'order_limit', 'no_items'].includes(kind),
      mailto: mailtoHref(supportEmail, t, {details: state.details || {}, order: state.order, ...context}),
    });

    async function lookup(values) {
      if (state.busy) return;
      // A new attempt: the answer to the last one (an outage, too many tries) no longer stands above the fields.
      view.clearMessage();
      const {value, errors} = validateDetails(values);
      view.showFieldErrors(errors, STEP_ONE);
      if (Object.keys(errors).length > 0) return;
      state.details = value;
      state.order = null;
      state.busy = true;
      view.setBusy('details', true);
      try {
        const response = await api.lookup({orderName: value.orderName, email: value.email});
        const outcome = outcomeOf(response.status, response.data);
        const order = outcome === 'ok' ? normalizeOrder(response.data) : null;
        if (order && order.items.length > 0) {
          state.order = order;
          const orderDay = shopDay(order.orderedAt);
          view.showComplaint({
            order: format(t('summary_order'), {order: order.orderName, date: formatDate(order.orderedAt, false)}),
            items: order.items,
            marketNotice: REPAIR_CHOICE_NOTICE_MARKETS.includes(order.marketCode) && !order.businessBuyer,
            minDay: orderDay,
            maxDay: today(),
          });
        } else if (order) failure('no_items');
        else if (outcome === 'invalid') {
          const fieldErrors = pick(serverFieldErrors(response.data), STEP_ONE);
          if (Object.keys(fieldErrors).length > 0) view.showFieldErrors(fieldErrors, STEP_ONE);
          else failure('failed');
        } else failure(outcome === 'ok' ? 'failed' : outcome);
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error;
        failure('failed');
      } finally {
        state.busy = false;
        view.setBusy('details', false);
      }
    }

    async function send(values) {
      if (state.busy || !state.order) return;
      view.clearMessage();
      const declared = complainedItems(state.order, values.selection);
      const {value: complaint, errors} = validateComplaint(values, {orderDay: shopDay(state.order.orderedAt), today: today()});
      // Fields first, the goods last: the focus lands on the first problem from the top.
      view.showFieldErrors(errors, STEP_TWO);
      view.showItemsError(declared.error || null);
      if (declared.error || Object.keys(errors).length > 0) return;
      const details = state.details;
      const key = fingerprint(details, complaint, declared.items);
      const body = {
        submissionId: submissions.idFor(key),
        orderName: details.orderName,
        email: details.email,
        customerName: details.customerName,
        phone: details.phone || null,
        items: declared.items,
        defectDescription: complaint.defectDescription,
        defectDiscoveredOn: complaint.defectDiscoveredOn || null,
        requestedRemedy: complaint.requestedRemedy,
        bankAccount: complaint.bankAccount || null,
      };
      const context = {complaint, items: declared.items};
      state.busy = true;
      view.setBusy('complaint', true);
      try {
        const response = await api.submit(body);
        const outcome = outcomeOf(response.status, response.data);
        if (outcome === 'ok' && response.data && typeof response.data.reference === 'string') {
          submissions.forget(key);
          const params = {
            order: text(response.data.orderName) || state.order.orderName,
            date: formatDate(text(response.data.receivedAt), true),
            reference: response.data.reference,
          };
          const due = isDay(text(response.data.dueOn)) ? format(t('done_due'), {due: formatDate(response.data.dueOn, false)}) : '';
          view.showDone({
            text: [format(t(response.status === 201 ? 'done_text' : 'done_again'), params), due].filter(Boolean).join(' '),
            photosMailto: photosHref(supportEmail, t, params),
          });
          return;
        }
        if (outcome === 'conflict') {
          // Another complaint went under this id before (a changed form after a lost answer); the next try is a new one.
          submissions.renew(key);
          failure('failed', context);
        } else if (outcome === 'invalid') {
          const fieldErrors = serverFieldErrors(response.data);
          const first = pick(fieldErrors, STEP_ONE);
          if (Object.keys(first).length > 0) {
            view.showDetails();
            view.showFieldErrors(first, STEP_ONE);
          } else if (fieldErrors.items || Object.keys(pick(fieldErrors, STEP_TWO)).length > 0) {
            view.showFieldErrors(pick(fieldErrors, STEP_TWO), STEP_TWO);
            view.showItemsError(fieldErrors.items || null);
          } else failure('failed', context);
        } else failure(outcome === 'ok' ? 'failed' : outcome, context);
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error;
        failure('failed', context);
      } finally {
        state.busy = false;
        view.setBusy('complaint', false);
      }
    }

    function back() {
      if (state.busy) return;
      state.order = null;
      view.showDetails();
    }

    const remedyChanged = remedy => view.showAccount(MONEY_REMEDIES.includes(remedy));

    return {lookup, send, back, remedyChanged, state};
  }

  function createDomView(root, t) {
    const q = selector => root.querySelector(selector);
    const parts = {
      form: q('[data-complaint-form]'),
      continue: q('[data-complaint-continue]'),
      message: q('[data-complaint-message]'),
      messageText: q('[data-complaint-message-text]'),
      fallback: q('[data-complaint-fallback]'),
      fallbackText: q('[data-complaint-fallback-text]'),
      mailto: q('[data-complaint-mailto]'),
      details: q('[data-complaint-details]'),
      summaryOrder: q('[data-complaint-summary-order]'),
      items: q('[data-complaint-items]'),
      itemsError: q('[data-complaint-error="items"]'),
      account: q('[data-complaint-account]'),
      marketNotice: q('[data-complaint-market-notice]'),
      send: q('[data-complaint-send]'),
      back: q('[data-complaint-back]'),
      done: q('[data-complaint-done]'),
      doneText: q('[data-complaint-done-text]'),
      photosMailto: q('[data-complaint-photos-mailto]'),
      template: q('[data-complaint-item-template]'),
    };
    const field = name => q(`[data-complaint-field="${name}"]`);
    const remedies = () => [...root.querySelectorAll('[data-complaint-remedy]')];
    const errorOf = name => q(`[data-complaint-error="${name}"]`);
    const errorText = code => t(`error_${code}`) || t('error_invalid');

    return {
      values: () => Object.fromEntries(STEP_ONE.map(name => [name, field(name)?.value ?? ''])),
      complaintValues: () => ({
        selection: [...parts.items.querySelectorAll('[data-line-item-id]')].map(node => ({
          lineItemId: node.dataset.lineItemId,
          checked: node.querySelector('[data-complaint-item-check]').checked,
          quantity: Number(node.querySelector('[data-complaint-item-quantity]').value) || undefined,
        })),
        defectDescription: field('defectDescription')?.value ?? '',
        defectDiscoveredOn: field('defectDiscoveredOn')?.value ?? '',
        requestedRemedy: remedies().find(input => input.checked)?.value ?? '',
        bankAccount: field('bankAccount')?.value ?? '',
      }),
      /** Shows the errors of the named fields and clears the others of them; focus goes to the first wrong field. */
      showFieldErrors(errors, names) {
        let first = null;
        for (const name of names) {
          const inputs = name === 'requestedRemedy' ? remedies() : [field(name)].filter(Boolean);
          const node = errorOf(name);
          const code = errors[name];
          if (node) {
            node.hidden = !code;
            node.textContent = code ? errorText(code) : '';
          }
          for (const input of inputs) {
            if (code) input.setAttribute('aria-invalid', 'true');
            else input.removeAttribute('aria-invalid');
          }
          if (code && !first) first = inputs[0];
        }
        if (first) first.focus();
      },
      clearMessage() { parts.message.hidden = true; },
      showMessage(kind, {fallback, mailto}) {
        parts.messageText.textContent = t(kind) || t('failed');
        parts.fallback.hidden = !fallback;
        parts.fallbackText.textContent = t('fallback');
        parts.mailto.href = mailto;
        parts.message.hidden = false;
        parts.message.focus();
      },
      showComplaint({order, items, marketNotice, minDay, maxDay}) {
        parts.message.hidden = true;
        parts.summaryOrder.textContent = order;
        parts.items.replaceChildren();
        for (const item of items) {
          const node = parts.template.content.firstElementChild.cloneNode(true);
          node.dataset.lineItemId = item.lineItemId;
          // One item is the complaint's goods; with more the customer ticks the faulty ones.
          node.querySelector('[data-complaint-item-check]').checked = items.length === 1;
          node.querySelector('[data-complaint-item-title]').textContent = itemLabel(item);
          const meta = node.querySelector('[data-complaint-item-meta]');
          if (item.sku) {
            meta.textContent = format(t('item_sku'), {sku: item.sku});
            meta.hidden = false;
          }
          const image = node.querySelector('[data-complaint-item-image]');
          if (item.imageUrl) {
            image.src = item.imageUrl;
            image.hidden = false;
          }
          const select = node.querySelector('[data-complaint-item-quantity]');
          if (item.quantity > 1) {
            node.querySelector('[data-complaint-item-quantity-field]').hidden = false;
            select.setAttribute('aria-label', format(t('item_quantity'), {title: item.title}));
            for (let count = 1; count <= item.quantity; count += 1) {
              const option = document.createElement('option');
              option.value = String(count);
              option.textContent = String(count);
              select.append(option);
            }
          }
          parts.items.append(node);
        }
        parts.itemsError.hidden = true;
        parts.marketNotice.hidden = !marketNotice;
        const date = field('defectDiscoveredOn');
        if (date) {
          if (minDay) date.min = minDay; else date.removeAttribute('min');
          if (maxDay) date.max = maxDay; else date.removeAttribute('max');
        }
        this.showAccount(MONEY_REMEDIES.includes(remedies().find(input => input.checked)?.value));
        parts.form.hidden = true;
        parts.details.hidden = false;
        parts.details.focus();
      },
      showAccount(visible) {
        parts.account.hidden = !visible;
        if (!visible) this.showFieldErrors({}, ['bankAccount']);
      },
      showItemsError(code) {
        parts.itemsError.hidden = !code;
        parts.itemsError.textContent = code ? t(code) : '';
        if (code) parts.itemsError.focus?.();
      },
      showDetails() {
        parts.details.hidden = true;
        parts.message.hidden = true;
        parts.form.hidden = false;
        field('orderName')?.focus();
      },
      showDone({text: message, photosMailto}) {
        parts.message.hidden = true;
        parts.details.hidden = true;
        parts.form.hidden = true;
        parts.doneText.textContent = message;
        parts.photosMailto.href = photosMailto;
        parts.done.hidden = false;
        parts.done.focus();
      },
      setBusy(step, busy) {
        const details = step === 'details';
        const button = details ? parts.continue : parts.send;
        button.disabled = busy;
        button.textContent = t(busy ? (details ? 'checking' : 'sending') : (details ? 'continue' : 'send'));
        parts.back.disabled = busy;
        (details ? parts.form : parts.details).setAttribute('aria-busy', busy ? 'true' : 'false');
      },
      bind({onLookup, onSend, onBack, onRemedy}) {
        parts.form.addEventListener('submit', event => {
          event.preventDefault();
          onLookup();
        });
        parts.details.addEventListener('submit', event => {
          event.preventDefault();
          onSend();
        });
        parts.back.addEventListener('click', () => onBack());
        for (const input of remedies()) input.addEventListener('change', () => onRemedy(input.value));
      },
      ready() { parts.form.hidden = false; },
    };
  }

  function start(root) {
    const {t, formatDate, storage, uuid, fetchImpl} = window.BestioPublicForm.pageContext(root, '[data-complaint-i18n]');
    const view = createDomView(root, t);
    const flow = createComplaintFlow({
      api: createApi({baseUrl: root.dataset.apiBaseUrl, path: API_PATH, fetchImpl}),
      view,
      submissions: createSubmissions({storage, uuid}),
      supportEmail: root.dataset.supportEmail || '',
      t,
      formatDate,
    });
    view.bind({
      onLookup: () => flow.lookup(view.values()),
      onSend: () => flow.send(view.complaintValues()),
      onBack: () => flow.back(),
      onRemedy: remedy => flow.remedyChanged(remedy),
    });
    view.ready();
    window.BestioPublicForm.prefillLinkedOrder(root, window.location.hash);
  }

  window.BestioComplaintForm = {
    createApi: options => createApi({path: API_PATH, ...options}), outcomeOf, validateDetails, validateComplaint, normalizeOrder,
    complainedItems, createSubmissions, mailtoHref, photosHref, serverFieldErrors, createComplaintFlow, shopDay, isDay, REMEDIES,
    MONEY_REMEDIES, REPAIR_CHOICE_NOTICE_MARKETS, LIMITS, MAILTO_LIMIT, format,
  };
  if (typeof document === 'undefined') return;
  const root = document.querySelector('[data-bestio-complaint]');
  if (root) start(root);
})();
